import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart2, List, Users, UserCircle, Database, TrendingUp } from 'lucide-react';
import Dashboard from './Dashboard';
import WaitlistManagement from './WaitlistManagement';
import UserManagement from './UserManagement';
import './Admin.css';

import OrganizationDashboard from './OrganizationDashboard';
import AICostsDashboard from './AICostsDashboard';
// Phase 3 components — uncomment imports as each is built:
// import GrowthDashboard from './GrowthDashboard';

/**
 * Tab configuration for the admin dashboard.
 *
 * key:   URL segment and internal identifier
 * label: Display text in the tab bar
 * icon:  lucide-react icon component
 */
const TABS = [
  { key: 'overview',      label: 'Overview',      icon: BarChart2 },
  { key: 'waitlist',      label: 'Waitlist',       icon: List },
  { key: 'users',         label: 'Users',          icon: UserCircle },
  { key: 'organizations', label: 'Organizations',  icon: Users },
  { key: 'costs',         label: 'AI & Costs',     icon: Database },
  { key: 'growth',        label: 'Growth',         icon: TrendingUp },
];

const Placeholder = ({ name }) => (
  <div className="placeholder-section">
    <h2>{name}</h2>
    <p>This section is under construction.</p>
  </div>
);

const Admin = ({ section }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('overview');

  // Determine active section from props or URL
  useEffect(() => {
    if (section) {
      setActiveSection(section);
    } else {
      const path = location.pathname.split('/');
      const urlSection = path[path.length - 1];
      if (urlSection && urlSection !== 'admin') {
        setActiveSection(urlSection);
      } else {
        setActiveSection('overview');
      }
    }
  }, [section, location]);

  const navigateTo = (sectionKey) => {
    navigate(`/admin/${sectionKey === 'overview' ? '' : sectionKey}`);
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'waitlist':
        return <WaitlistManagement />;
      case 'users':
        return <UserManagement />;
      case 'organizations':
        return <OrganizationDashboard />;
      case 'costs':
        return <AICostsDashboard />;
      case 'growth':
        // Replace with <GrowthDashboard /> when built (Phase 3.5)
        return <Placeholder name="Growth" />;
      case 'overview':
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
      </div>

      <div className="admin-navigation">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            className={`nav-button ${activeSection === key ? 'active' : ''}`}
            onClick={() => navigateTo(key)}
          >
            <Icon size={20} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className="admin-content">
        {renderSection()}
      </div>
    </div>
  );
};

export default Admin;
